import { useCallback } from 'react';
import { useHistory } from 'react-router';
import { routeMap } from '../../constants';

type ReferralRoute = keyof typeof routeMap;

/** utiltiy fn which replaces `:param` in a route path string with provided params */
export const replaceRouteParams = <
  T extends string,
  Params extends Record<string, string>,
>(
  route: T,
  params: Params &
    Record<
      Extract<T, `:${string}`> extends `:${infer Param}` ? Param : never,
      string
    >,
): string => {
  // Get all parameter names from the route using regex
  const paramNames = [...route.matchAll(/:([\w]+)\??/g)].map(
    (match) => match[1],
  );

  return paramNames.reduce((path, param) => {
    // If the parameter exists in params, replace it
    if (params[param]) {
      return path.replace(new RegExp(`:${param}\\??`), params[param]);
    }
    // If it's an optional parameter (has ?) and not provided, remove the optional parameter entirely
    return path.replace(new RegExp(`/:${param}\\?`), '');
  }, route);
};

/** navigate to createReferral, editReferral, viewReferral paths */
export default function useGoToReferralRoute() {
  const { location, push: historyPush } = useHistory();

  const goToRoute = useCallback(
    (route: ReferralRoute, params: Record<string, string | undefined> = {}) => {
      // Filter out undefined params to handle optional parameters
      const definedParams = Object.entries(params).reduce(
        (acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, string>,
      );

      historyPush(
        location?.pathname.replace(/referrals.*/, () =>
          replaceRouteParams(routeMap[route], definedParams),
        ),
      );
    },
    [location.pathname, historyPush],
  );

  return {
    /** navigate to create referral path, provide a crmId if available */
    createReferral: (crmId?: string) => goToRoute('createReferral', { crmId }),
    /** navigate to edit referral path */
    editReferral: (tackleId: string) => goToRoute('editReferral', { tackleId }),
    /** navigate to view referral path */
    viewReferral: (tackleId: string) => goToRoute('viewReferral', { tackleId }),
  };
}
